import { useEffect, useMemo, useRef, useState } from 'react';
import { openDB } from 'idb';
import { useSearchParams } from 'react-router-dom';
import { Tab, Tabs, useTheme } from '@mui/material';
import Popup from 'reactjs-popup';
import { fetchReport, postCopyConfig, ReportResponse, ReportTab } from '../../../Api/Report/reportApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { ContactUsForm } from '../../Compounds/ContactUs/ContactUsForm';
import { TabPanel } from '../../Atoms/TabPanel';
import { DEFAULT_ADVANCE_BUTTON_STYLE, MODAL_CONTENT_STYLE } from '../../../utils/styleHelpers';
import { Icon } from '../../Atoms/Icon';
import { TextBlock } from '../../Atoms/Text';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { PageCard } from '../../Compounds/CardWithTitle';
import { ReportDataTable } from './ReportDataTable';
import { ReportExecutiveSummary } from './ReportExecutiveSummary';
import { ReportTabsViewer } from './ReportTabsViewer';
import { DownloadExcelButton } from './DownloadExcelButton';
import { useReportWorkbook } from './useReportWorkbook';
import { resolveReportTabs } from '../../../utils/reportTabs';
import { workbookFileName } from '../../../utils/reportWorkbook';

const contactModalStyle = { ...MODAL_CONTENT_STYLE, width: '400px' };

const REPORT_CACHE_PREFIX = 'upstack_report_';
const REPORT_CACHE_TTL_MS = 15_000; // 15 seconds

interface ReportCacheEntry {
  data: ReportResponse;
  expiresAt: number;
}

let reportDbPromise: ReturnType<typeof openDB> | null = null;
const getReportDb = () => {
  if (!reportDbPromise) {
    reportDbPromise = openDB('upstack_reports', 1, {
      upgrade(db) {
        db.createObjectStore('reports');
      }
    });
  }
  return reportDbPromise;
};

function formatViewDate(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return 'Shared View';
  }
}

type ErrorType = 'unauthorized' | 'not_found' | 'generic' | null;

const tabsOf = (data: ReportResponse | null): ReportTab[] =>
  data
    ? resolveReportTabs(data.tabs, {
        tab_name: data.view_name ?? 'View',
        table_type: data.view_type ?? 'inventory',
        view_config: data.view_config
      })
    : [];

export const ReportPage = () => {
  const [searchParams] = useSearchParams();
  const [reportData, setReportData] = useState<ReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [cardCollapsed, setCardCollapsed] = useState(false);
  const theme = useTheme();

  const scheduleSettingId = searchParams.get('schedule_setting_id') || '';
  const scheduleSnapshotId = searchParams.get('schedule_snapshot_id') || '';
  const authToken = searchParams.get('auth_token') || '';
  const copyToken = searchParams.get('copy_token') || '';
  const portalUserId = searchParams.get('portal_user_id') || '';
  // Read only once the report has loaded: an Orders report saved before Orders had views has none to add to My Views.
  const offersCopy =
    !!copyToken &&
    !!portalUserId &&
    (reportData?.view_type !== 'order' || reportData?.view_config?.customView === true);

  const { setSnack } = useSnackbar();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleCopyConfig = async () => {
    setCopyStatus('loading');
    const result = await postCopyConfig(scheduleSettingId, copyToken, portalUserId);
    if (result.error) {
      setCopyStatus('error');
      setSnack({ open: true, type: 'error', message: 'Failed to copy view. Please try again.' });
    } else {
      setCopyStatus('success');
      setSnack({ open: true, type: 'success', message: 'View successfully copied to My Views.' });
    }
  };

  const cacheKey = `${REPORT_CACHE_PREFIX}${scheduleSnapshotId}`;

  useEffect(() => {
    const loadReport = async () => {
      if (!scheduleSettingId || !scheduleSnapshotId || !authToken) {
        setErrorType('unauthorized');
        setLoading(false);
        return;
      }

      // Check IndexedDB first — token is single-use so we cache on first success
      try {
        const db = await getReportDb();
        const cached = (await db.get('reports', cacheKey)) as ReportCacheEntry | undefined;
        if (cached) {
          const sessionKey = `${cacheKey}_session`;
          const isSessionRefresh = sessionStorage.getItem(sessionKey) === 'active';
          if (Date.now() < cached.expiresAt || isSessionRefresh) {
            setReportData(cached.data);
            setLoading(false);
            return;
          } else {
            await db.delete('reports', cacheKey);
          }
        }
      } catch {
        // IDB unavailable — proceed to fetch
      }

      const result = await fetchReport(scheduleSettingId, scheduleSnapshotId, authToken);

      if (result.data) {
        try {
          const db = await getReportDb();
          const entry: ReportCacheEntry = { data: result.data, expiresAt: Date.now() + REPORT_CACHE_TTL_MS };
          await db.put('reports', entry, cacheKey);
          sessionStorage.setItem(`${cacheKey}_session`, 'active');
        } catch {
          // IDB write failed — report still usable in-memory
        }
        setReportData(result.data);
      } else {
        const status = result.error.errorResponse?.response?.status;
        if (status === 401) {
          setErrorType('unauthorized');
        } else if (status === 404) {
          setErrorType('not_found');
        } else {
          setErrorType('generic');
        }
      }
      setLoading(false);
    };

    loadReport();
  }, []);

  const viewDate = reportData ? formatViewDate(reportData.snapshot.created_at) : '';
  const reportTitle = reportData?.view_name || 'View';
  const viewName = `${reportTitle} - ${viewDate}`;
  const tabs = useMemo(() => tabsOf(reportData), [reportData]);
  const isCombined = tabs.length > 1;
  const singleTabContent = tabs[0]?.content ?? reportData?.snapshot.content;
  const rowCount = singleTabContent?.length ?? 0;
  const workbook = useReportWorkbook(tabs, workbookFileName(reportTitle, viewDate), scheduleSnapshotId);
  const { ready: workbookReady, download: downloadWorkbookFile } = workbook;
  const workbookStarted = useRef(false);
  const shownCount = workbook.counts[workbook.activeIndex] ?? rowCount;

  useEffect(() => {
    if (!isCombined || !workbookReady || workbookStarted.current) return;
    workbookStarted.current = true;
    void downloadWorkbookFile();
  }, [isCombined, workbookReady, downloadWorkbookFile]);

  return (
    <div className="min-h-screen bg-main-300">
      <title>UPSTACK Views</title>
      <div className="w-11/12 m-auto">
        {/* Dark header banner — matches BenchmarkPage */}
        <div className="px-8 py-10 my-8 rounded flex flex-row justify-between items-end align-bottom bg-nav-50">
          <div className="w-2/3">
            <TextBlock
              size="md"
              color="headerYellow"
              className="mb-4">
              UPSTACK Views
            </TextBlock>
            <TextBlock
              size="sm14"
              color="white">
              {loading ? null : offersCopy ? (
                <>
                  A colleague of yours has scheduled a reoccurring send of a custom data view for your organization from
                  the UPSTACK Portal. An <b>automatic download</b> of the shared view has begun you can also select to
                  &ldquo;Copy to My Views&rdquo; to have it added to your portal. Please ensure that download completes
                  as you won&apos;t be able to access this page again.
                </>
              ) : (
                <>
                  A colleague of yours has scheduled a reoccurring send of a custom data view for your organization from
                  the UPSTACK Portal. An <b>automatic download</b> of the shared view has begun so that you can work and
                  interact with the data as you need. Please ensure that download completes as you won&apos;t be able to
                  access this page again.
                </>
              )}
            </TextBlock>
          </div>
          <div className="flex flex-col gap-2 items-end">
            {loading ? null : offersCopy ? (
              <button
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                disabled={copyStatus === 'loading' || copyStatus === 'success'}
                onClick={handleCopyConfig}>
                {copyStatus === 'loading'
                  ? 'Copying...'
                  : copyStatus === 'success'
                    ? 'Copied to My Views'
                    : copyStatus === 'error'
                      ? 'Copy Failed — Try Again'
                      : 'Copy to My Views'}
              </button>
            ) : (
              <button
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                onClick={() => setContactOpen(true)}>
                Contact UPSTACK
              </button>
            )}
            <Popup
              contentStyle={contactModalStyle}
              closeOnDocumentClick={false}
              modal={true}
              open={contactOpen}
              onClose={() => setContactOpen(false)}>
              <ContactUsForm onClose={() => setContactOpen(false)} />
            </Popup>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded-lg p-32 my-6">
            <ComponentSpinner />
          </div>
        )}

        {!loading && errorType === 'unauthorized' && (
          <div className="bg-white rounded-lg p-12 text-center my-6">
            <Icon
              type="alert"
              className="mx-auto mb-4 !w-16 !h-16"
            />
            <TextBlock
              size="lg"
              weight="semibold"
              className="mb-2">
              Link No Longer Available
            </TextBlock>
            <TextBlock
              size="sm14"
              color="grey8"
              className="max-w-md mx-auto">
              This report link has already been used or is invalid. Each link provides one-time access to the shared
              view. If you need access to this data again, please ask the sender to generate a new report.
            </TextBlock>
          </div>
        )}

        {!loading && errorType === 'not_found' && (
          <div className="bg-white rounded-lg p-12 text-center my-6">
            <Icon
              type="alert"
              className="mx-auto mb-4 h-10 w-10"
            />
            <TextBlock
              size="lg"
              weight="semibold"
              className="mb-2">
              Report Not Found
            </TextBlock>
            <TextBlock
              size="sm14"
              color="grey8"
              className="max-w-md mx-auto">
              The report associated with this link could not be found. It may have been removed. Please contact the
              sender if you believe this is an error.
            </TextBlock>
          </div>
        )}

        {!loading && errorType === 'generic' && (
          <div className="bg-white rounded-lg p-12 text-center my-6">
            <Icon
              type="alert"
              className="mx-auto mb-4 h-10 w-10"
            />
            <TextBlock
              size="lg"
              weight="semibold"
              className="mb-2">
              Something Went Wrong
            </TextBlock>
            <TextBlock
              size="sm14"
              color="grey8"
              className="max-w-md mx-auto">
              There was an error loading your report. Please try refreshing the page.
            </TextBlock>
          </div>
        )}

        {!loading && reportData?.snapshot.ai_summary && (
          <ReportExecutiveSummary summary={reportData.snapshot.ai_summary} />
        )}

        {!loading && reportData && (
          <PageCard
            childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
            title={`UPSTACK Views - ${shownCount.toLocaleString()} item${shownCount !== 1 ? 's' : ''}`}
            enableCollapse
            collapsed={false}
            onCollapseClick={setCardCollapsed}
            cardActions={
              <div className="flex items-center gap-3">
                {isCombined && (
                  <TextBlock
                    size="sm14"
                    color="grey8">{`Run: ${viewDate}`}</TextBlock>
                )}
                <DownloadExcelButton
                  sheetCount={tabs.length}
                  disabled={!workbook.ready || workbook.downloading || cardCollapsed}
                  onDownload={() => void workbook.download()}
                />
              </div>
            }>
            <>
              {isCombined ? (
                <ReportTabsViewer
                  tabs={tabs}
                  content={reportData.snapshot.content as Record<string, unknown>[]}
                  workbook={workbook}
                  toolbarVariant="minimal"
                  reportName={viewName}
                />
              ) : (
                <>
                  <Tabs
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: '#F9F9F9',
                      margin: '-20px',
                      '.Mui-selected': {
                        backgroundColor: 'white',
                        color: theme.palette.primary.main
                      }
                    }}
                    TabIndicatorProps={{
                      sx: {
                        backgroundColor: 'white',
                        height: 2,
                        borderRadius: 1
                      }
                    }}
                    value={0}>
                    <Tab
                      sx={{ textTransform: 'none', fontSize: '.8rem', color: 'black' }}
                      label={viewName}
                    />
                  </Tabs>
                  <TabPanel
                    value={0}
                    index={0}>
                    <ReportDataTable
                      viewType={reportData.view_type}
                      content={singleTabContent as Record<string, unknown>[]}
                      viewConfig={reportData.view_config}
                      viewName={viewName}
                      toolbarVariant="minimal"
                      autoExportOnLoad
                      apiRef={workbook.apiRefs[0]}
                      onFilteredRowCountChange={(count) => workbook.onCount(0, count)}
                    />
                  </TabPanel>
                </>
              )}
            </>
          </PageCard>
        )}
        <div className="h-2" />
      </div>
    </div>
  );
};
